import React from "react";
import { CompetitionAutocomplete } from "./CompetitionAutocomplete";
import { ROBOTS_TXT_STATUS_LABELS } from "../../../constants/robots-txt-status";

export { ROBOTS_TXT_STATUS_LABELS };

export interface CompetitionCount {
  id: string;
  name: string;
  code: string | null;
  count: number;
}

export interface RegionCount {
  region: string;
  label: string;
  count: number;
}

interface SourcesFiltersProps {
  competitionCounts: CompetitionCount[];
  regionCounts: RegionCount[];
  sourceTypes: { value: string; label: string }[];
  selectedCompetitionId: string;
  selectedRegion: string;
  selectedSourceType: string;
  neverChecked: boolean;
  robotsTxtStatusCounts?: Record<string, number>;
  selectedRobotsTxtStatus?: string;
  isLoading?: boolean;
  onCompetitionChange: (competitionId: string) => void;
  onRegionChange: (region: string) => void;
  onSourceTypeChange: (sourceType: string) => void;
  onNeverCheckedChange: (checked: boolean) => void;
  onRobotsTxtStatusChange?: (status: string) => void;
  onClearFilters: () => void;
}

/**
 * Loading skeleton for a single dropdown filter.
 */
const DropdownSkeleton = () => (
  <div className="w-52 h-10 bg-neutral-200 rounded-md animate-pulse" />
);

/**
 * Dropdown-based filters for sources list.
 * Provides Competition and Source Type dropdowns.
 *
 * @see docs/specifications/2026-01-22-grants-sources-competition-filter.md
 */
const SourcesFilters: React.FC<SourcesFiltersProps> = ({
  competitionCounts,
  regionCounts,
  sourceTypes,
  selectedCompetitionId,
  selectedRegion,
  selectedSourceType,
  neverChecked,
  robotsTxtStatusCounts,
  selectedRobotsTxtStatus = "",
  isLoading = false,
  onCompetitionChange,
  onRegionChange,
  onSourceTypeChange,
  onNeverCheckedChange,
  onRobotsTxtStatusChange,
  onClearFilters,
}) => {
  const hasActiveFilters = selectedCompetitionId || selectedRegion || selectedSourceType || neverChecked || selectedRobotsTxtStatus;

  // Calculate total sources for "All competitions" option
  const totalSources = competitionCounts.reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Competition dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-neutral-700">
            Konkurs:
          </label>
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <CompetitionAutocomplete
              competitions={competitionCounts}
              selectedCompetitionId={selectedCompetitionId}
              onCompetitionChange={onCompetitionChange}
              totalSources={totalSources}
            />
          )}
        </div>

        {/* Region dropdown */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="region-filter"
            className="text-sm font-medium text-neutral-700"
          >
            Województwo:
          </label>
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <select
              id="region-filter"
              value={selectedRegion}
              onChange={(e) => onRegionChange(e.target.value)}
              className="w-52 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Wszystkie województwa</option>
              {regionCounts.map((rc) => (
                <option key={rc.region} value={rc.region}>
                  {rc.label} ({rc.count})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Source type dropdown */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="source-type-filter"
            className="text-sm font-medium text-neutral-700"
          >
            Typ:
          </label>
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <select
              id="source-type-filter"
              value={selectedSourceType}
              onChange={(e) => onSourceTypeChange(e.target.value)}
              className="w-40 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Wszystkie typy</option>
              {sourceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* robots.txt status dropdown */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="robots-txt-status-filter"
            className="text-sm font-medium text-neutral-700"
          >
            robots.txt:
          </label>
          {isLoading ? (
            <DropdownSkeleton />
          ) : (
            <select
              id="robots-txt-status-filter"
              data-testid="robots-txt-status-filter"
              value={selectedRobotsTxtStatus}
              onChange={(e) => onRobotsTxtStatusChange?.(e.target.value)}
              className="w-48 px-3 py-2 border border-neutral-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Wszystkie statusy</option>
              {Object.entries(ROBOTS_TXT_STATUS_LABELS).map(([status, label]) => {
                const count = robotsTxtStatusCounts?.[status] ?? 0;
                return (
                  <option key={status} value={status}>
                    {label} ({count})
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Never checked checkbox */}
        <div className={`flex items-center gap-2 min-h-[40px]${isLoading ? ' opacity-50' : ''}`}>
          <input
            type="checkbox"
            id="never-checked-filter"
            checked={neverChecked}
            disabled={isLoading}
            onChange={(e) => onNeverCheckedChange(e.target.checked)}
            title="Pokaż tylko źródła, które nigdy nie były sprawdzone"
            className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
          />
          <label
            htmlFor="never-checked-filter"
            className="text-sm font-medium text-neutral-700 cursor-pointer select-none"
          >
            Nigdy niesprawdzone
          </label>
        </div>

        {/* Clear filters button */}
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-md hover:bg-neutral-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Wyczyść wszystkie filtry"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Wyczyść
          </button>
        )}
      </div>
    </div>
  );
};

export default SourcesFilters;
